import React, { useCallback, useMemo } from 'react';

import { type ForYouConfig } from '@atlassian/global-side-navigation/types';

import { HOME, WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	GO_HOME_SHORTCUT,
	GO_TO_RECENT_SHORTCUT,
	GO_TO_STARRED_SHORTCUT,
	UNIVERSAL_GO_HOME_SHORTCUT,
	UNIVERSAL_GO_TO_RECENT_SHORTCUT,
	UNIVERSAL_GO_TO_STARRED_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { HomeOnboardingTakeoverV2HomeNudge } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/HomeOnboardingTakeoverV2HomeNudge.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { EditorForYouSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorForYouSpotlight.ts';

import { KeyboardShortcutWrapper } from '../KeyboardShortcutWrapper.tsx';
import { useRecentItems, useRecentsFilterConfig } from './useRecentItems.tsx';
import { useStarredItems } from './useStarredItems.tsx';

const isOnHomeSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl() || routeUrl === WIKI.toUrl();
	},
};

export const useForYouConfig = (): ForYouConfig => {
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const isSelected = useRouteUrl(isOnHomeSelector);
	const homeUrl = HOME.toUrl();
	const ForYouItemWrapper = useCallback(
		({ children }: { children: React.ReactNode }) => (
			<KeyboardShortcutWrapper
				href={homeUrl}
				deprecatedKeyboardShortcut={
					!(isLiveEditMode || isOnEditRoute) ? GO_HOME_SHORTCUT : undefined
				}
				keyboardShortcut={UNIVERSAL_GO_HOME_SHORTCUT}
				isAppNavigationShortcut
			>
				<HomeOnboardingTakeoverV2HomeNudge>
					<EditorForYouSpotlight>{children}</EditorForYouSpotlight>
				</HomeOnboardingTakeoverV2HomeNudge>
			</KeyboardShortcutWrapper>
		),
		[homeUrl, isLiveEditMode, isOnEditRoute],
	);

	const recentsFilterConfig = useRecentsFilterConfig();
	const recentViewAllUrl = HOME.toUrl({ view: 'recent' });
	const RecentsItemWrapper = useCallback(
		({ children }: { children: React.ReactNode }) => (
			<KeyboardShortcutWrapper
				deprecatedKeyboardShortcut={
					!(isLiveEditMode || isOnEditRoute) ? GO_TO_RECENT_SHORTCUT : undefined
				}
				keyboardShortcut={UNIVERSAL_GO_TO_RECENT_SHORTCUT}
				isAppNavigationShortcut
			>
				{children}
			</KeyboardShortcutWrapper>
		),
		[isLiveEditMode, isOnEditRoute],
	);

	const starredViewAllUrl = HOME.toUrl({ view: 'starred' });
	const StarredItemWrapper = useCallback(
		({ children }: { children: React.ReactNode }) => (
			<KeyboardShortcutWrapper
				deprecatedKeyboardShortcut={
					!(isLiveEditMode || isOnEditRoute) ? GO_TO_STARRED_SHORTCUT : undefined
				}
				keyboardShortcut={UNIVERSAL_GO_TO_STARRED_SHORTCUT}
				isAppNavigationShortcut
			>
				{children}
			</KeyboardShortcutWrapper>
		),
		[isLiveEditMode, isOnEditRoute],
	);

	return useMemo<ForYouConfig>(
		() => ({
			forYouItem: {
				menuItemId: 'yourWork',
				url: homeUrl,
				isSelected,
				ItemWrapper: ForYouItemWrapper,
			},
			recent: {
				menuItemId: 'recent',
				useItems: useRecentItems,
				viewAllUrl: recentViewAllUrl,
				isSearchable: true,
				filterConfig: recentsFilterConfig,
				ItemWrapper: RecentsItemWrapper,
			},
			starred: {
				menuItemId: 'starred',
				useItems: useStarredItems,
				viewAllUrl: starredViewAllUrl,
				isSearchable: true,
				ItemWrapper: StarredItemWrapper,
			},
		}),
		[
			homeUrl,
			isSelected,
			ForYouItemWrapper,
			RecentsItemWrapper,
			StarredItemWrapper,
			recentViewAllUrl,
			recentsFilterConfig,
			starredViewAllUrl,
		],
	);
};
